import { useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Button,
  TableRow,
  TableCell,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewListIcon from '@mui/icons-material/ViewList';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import DataTable from '@/components/DataTable/DataTable';
import TreeTable from '@/components/TreeTable/TreeTable';
import { FacilityGroupsApi } from '@/api/clients/facility-groups/facility-groups.api';
import { useGetFacilityGroupTree } from '@/api/hooks/facility-groups/useGetFacilityGroupTree';
import { useConfirmation } from '@/context/ConfirmationContext';
import FacilityGroupFormDialog from '@/views/Organizations/FacilityGroups/FacilityGroupFormDialog';
import AssignFacilitiesDialog from '@/views/Organizations/FacilityGroups/AssignFacilitiesDialog';

const VIEW_MODE_STORAGE_KEY = 'facilityGroups.viewMode';

const FacilityGroupsTab = ({ organizationId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dataTableRef = useRef(null);
  const { requestConfirmation } = useConfirmation();
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigningGroup, setAssigningGroup] = useState(null);
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem(VIEW_MODE_STORAGE_KEY) || 'list',
  );
  const {
    treeData,
    loading: treeLoading,
    refetch: fetchTreeData,
  } = useGetFacilityGroupTree(organizationId, { enabled: viewMode === 'tree' });

  const handleViewModeChange = (_, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, newMode);
    }
  };

  const refreshData = useCallback(() => {
    if (viewMode === 'list') {
      dataTableRef.current?.refresh();
    } else {
      fetchTreeData();
    }
  }, [viewMode, fetchTreeData]);

  const handleDelete = async (groupId, groupName) => {
    const confirmMessage = t('facilityGroups.confirmDelete', {
      name: groupName,
    });

    requestConfirmation(confirmMessage, async () => {
      setLoading(true);
      try {
        await FacilityGroupsApi.deleteFacilityGroup(organizationId, groupId);
        refreshData();
      } catch (err) {
        console.error('Error deleting facility group:', err);
      } finally {
        setLoading(false);
      }
    });
  };

  const handleNavigateToDetails = (group) => {
    navigate(
      `/admin/updateOrganization/${organizationId}/facility-groups/${group.id}`,
    );
  };

  const handleQuickEdit = (group) => {
    setEditingGroup(group);
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingGroup(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingGroup(null);
  };

  const handleDialogSuccess = (newGroupId) => {
    handleCloseDialog();
    if (newGroupId) {
      navigate(
        `/admin/updateOrganization/${organizationId}/facility-groups/${newGroupId}`,
      );
    } else {
      refreshData();
    }
  };

  const handleAssign = (group) => {
    setAssigningGroup(group);
    setAssignDialogOpen(true);
  };

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setAssigningGroup(null);
  };

  const handleSaveAssignments = async (facilityIds) => {
    setLoading(true);
    try {
      await FacilityGroupsApi.assignFacilities(
        organizationId,
        assigningGroup.id,
        facilityIds,
      );
      handleCloseAssignDialog();
      refreshData();
    } catch {
      toast.error(t('facilityGroups.assign.error'));
    } finally {
      setLoading(false);
    }
  };

  const columnFilters = useMemo(
    () => [
      {
        key: 'search',
        label: t('facilityGroups.search'),
        filterType: 'search',
        placeholder: t('facilityGroups.searchPlaceholder'),
        debounce: true,
        debounceDelay: 300,
      },
    ],
    [t],
  );

  const columns = [
    {
      key: 'name',
      label: t('facilityGroups.table.name'),
      sortable: true,
    },
    {
      key: 'description',
      label: t('facilityGroups.table.description'),
      render: (value) => value || '-',
    },
    {
      key: 'parent_name',
      label: t('facilityGroups.table.parentGroup'),
      render: (value) => value || '-',
    },
    {
      key: 'depth',
      label: t('facilityGroups.table.hierarchyLevel'),
      sortable: true,
    },
    {
      key: 'facility_count',
      label: t('facilityGroups.table.facilities'),
      sortable: true,
      render: (value) => value || 0,
    },
    {
      key: 'children_count',
      label: t('facilityGroups.table.children'),
      sortable: true,
      render: (value) => value || 0,
    },
    {
      key: 'actions',
      label: t('facilityGroups.table.actions'),
      sortable: false,
      align: 'right',
      render: (_, row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title={t('facilityGroups.assignTooltip')}>
            <IconButton
              size='small'
              color='primary'
              onClick={(e) => {
                e.stopPropagation();
                handleAssign(row);
              }}>
              <AccountTreeIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('facilityGroups.editTooltip')}>
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                handleNavigateToDetails(row);
              }}>
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('facilityGroups.quickEditTooltip')}>
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                handleQuickEdit(row);
              }}>
              <SettingsIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('facilityGroups.deleteTooltip')}>
            <IconButton
              size='small'
              color='error'
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.id, row.name);
              }}
              disabled={loading}>
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const renderActions = (row) => (
    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
      <Tooltip title={t('facilityGroups.assignTooltip')}>
        <IconButton
          size='small'
          color='primary'
          onClick={(e) => {
            e.stopPropagation();
            handleAssign(row);
          }}>
          <AccountTreeIcon fontSize='small' />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('facilityGroups.editTooltip')}>
        <IconButton
          size='small'
          onClick={(e) => {
            e.stopPropagation();
            handleNavigateToDetails(row);
          }}>
          <EditIcon fontSize='small' />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('facilityGroups.quickEditTooltip')}>
        <IconButton
          size='small'
          onClick={(e) => {
            e.stopPropagation();
            handleQuickEdit(row);
          }}>
          <SettingsIcon fontSize='small' />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('facilityGroups.deleteTooltip')}>
        <IconButton
          size='small'
          color='error'
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(row.id, row.name);
          }}
          disabled={loading}>
          <DeleteIcon fontSize='small' />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const treeColumns = [
    {
      key: 'name',
      label: t('facilityGroups.table.name'),
    },
    {
      key: 'facility_count',
      label: t('facilityGroups.table.facilities'),
      render: (value) => value || 0,
    },
    {
      key: 'actions',
      label: t('facilityGroups.table.actions'),
      align: 'right',
    },
  ];

  const toolbar = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
      }}>
      <Button
        variant='outlined'
        color='primary'
        startIcon={<AddIcon />}
        onClick={handleCreate}>
        {t('facilityGroups.createButton')}
      </Button>
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={handleViewModeChange}
        size='small'>
        <ToggleButton value='list'>
          <Tooltip title={t('facilityGroups.viewMode.list')}>
            <ViewListIcon />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value='tree'>
          <Tooltip title={t('facilityGroups.viewMode.tree')}>
            <AccountTreeOutlinedIcon />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );

  return (
    <Box sx={{ pt: 2 }}>
      {viewMode === 'list' ? (
        <DataTable
          ref={dataTableRef}
          columns={columns}
          endpoint={`/organizations/${organizationId}/facility-groups`}
          initialSort={{ key: 'depth', order: 'asc' }}
          pageSize={50}
          autoFetch={true}
          enableColumnPicker={true}
          enableSorting={true}
          enableFilters={true}
          columnFilters={columnFilters}
          externalLoading={loading}
          renderToolbar={() => toolbar}
          renderEmptyState={() => (
            <TableRow>
              <TableCell colSpan={columns.length} align='center' sx={{ py: 8 }}>
                <Typography variant='body1' color='text.secondary'>
                  {t('facilityGroups.noData')}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        />
      ) : (
        <TreeTable
          data={treeData}
          columns={treeColumns}
          renderActions={renderActions}
          onRowClick={handleNavigateToDetails}
          loading={treeLoading || loading}
          emptyMessage={t('facilityGroups.noData')}
          renderToolbar={() => toolbar}
        />
      )}

      <FacilityGroupFormDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSuccess={handleDialogSuccess}
        organizationId={organizationId}
        facilityGroup={editingGroup}
      />

      <AssignFacilitiesDialog
        open={assignDialogOpen}
        onClose={handleCloseAssignDialog}
        onSave={handleSaveAssignments}
        loading={loading}
        facilityGroup={assigningGroup}
        organizationId={organizationId}
      />
    </Box>
  );
};

export default FacilityGroupsTab;
